import { useEffect, useRef } from 'react';
import { createCart } from '@/api/use-add-to-cart';
import { createCartGuidCookie, getExistingCartGuid, getLoggedInStatus } from '@/utils';
import { LoggedInStatus } from '@/data/constants';
import { useStore } from '@/store/useStore';

/**
 * Hook to initialize cart for anonymous users on app load.
 * 
 * This acts as a client-side fallback in case SSR cart creation failed or
 * for client-side navigations. SSR creates the cart during server-side rendering
 * and sets the cookie in the response, so this hook typically finds an existing cart.
 * 
 * This ensures cart_id is available for analytics tracking from the first add-to-cart.
 */
export const useInitializeCart = () => {
  const hasInitialized = useRef(false);
  const setInitializedCartId = useStore((state) => state.setInitializedCartId);
  const cartData = useStore((state) => state.cartData);

  useEffect(() => {
    // Only run once
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initializeCart = async () => {
      // Only for anonymous users without an existing cart
      const isAnonymous = getLoggedInStatus() === LoggedInStatus.ANONYMOUS;
      const existingGuid = getExistingCartGuid();
      const cartIdFromSSR = cartData?.data?.guid;

      // Skip if cart already exists (from SSR, cookie, or prior session)
      if (existingGuid || cartIdFromSSR) {
        // If cart exists from SSR but not yet in Zustand store, populate it
        if (cartIdFromSSR && !existingGuid) {
          setInitializedCartId(cartIdFromSSR);
        }
        return;
      }

      if (isAnonymous) {
        try {
          const response = await createCart();
          if (response?.guid) {
            // Store in Zustand for immediate access by analytics
            setInitializedCartId(response.guid);
            // Also store in cookie for persistence across page loads
            createCartGuidCookie(response.guid);
          }
        } catch (error) {
          // Silently fail - cart will be created on first add-to-cart if needed
          console.warn('Cart initialization skipped:', error);
        }
      }
    };

    initializeCart();
  }, [setInitializedCartId, cartData]);
};

export default useInitializeCart;

